import { useState, useEffect, useMemo, useContext } from "react";
import {
  Button,
  Flex,
  Modal,
  Typography,
  App,
  Switch,
  Descriptions,
  Tooltip,
  Pagination,
} from "antd";

import PlusIcon from "@/icons/PlusIcon";
import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";

import EmployeesTable from "@/components/tables/EmployeesTable";
import EmployeeModal from "@/components/modals/EmployeeModal";
import CustomSelect from "@/components/inputs/CustomSelect";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import FiltersModal from "@/components/modals/FiltersModal";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/employees";
import { getUnitsOptions } from "@/api/units";
import { getPositionsOptions } from "@/api/positions";

import { SORT_KEYS, NOT_FILLED_OPTION } from "@/constants/employees";

const LOCAL_STORAGE_KEY = "employeesTableParams";

const { Title } = Typography;

const resolveWithNotFilled = (value, list) => {
  if (!value) return null;

  if (value === "notFilled") {
    return {
      uuid: "notFilled",
      label: "Не заполнено",
      name: "Не заполнено",
    };
  }

  return list.find(({ uuid }) => uuid === value);
};

// Страница сотрудников
// Содержит список сотрудников с возможностью добавления, редактирования и удаления
const Employees = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const initialTableParams = {
    pagination: {
      current: 1,
      pageSize: 10,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      search: null,
      units_uuids: [],
      position_uuid: null,
      unit_uuid: null,
      show_dismissed: false,
    },
  };
  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Списки для фильтров
  const [units, setUnits] = useState([]);
  const [positions, setPositions] = useState([]);

  const subdivisions = useMemo(() => {
    return units.filter((unit) => unit.unit_type?.is_hierarchical);
  }, [units]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempValueFilters, setTempValueFilters] = useState(null);

  const isAnyFilterApplied = useMemo(() => {
    const { filters } = tableParams;

    return Object.entries(filters).some(([key, value]) => {
      const initialValue = initialTableParams.filters[key];
      if (!initialValue && !value) return false;

      if (Array.isArray(value)) {
        return JSON.stringify(value) !== JSON.stringify(initialValue);
      }

      return value !== initialValue;
    });
    // eslint-disable-next-line
  }, [tableParams]);

  // Формируем массив выбранных фильтров для отдельной плашки
  const selectedFilters = useMemo(() => {
    const { position_uuid, unit_uuid, show_dismissed } = tableParams.filters;

    const position = resolveWithNotFilled(position_uuid, positions);
    const unit = subdivisions.find(({ uuid }) => uuid === unit_uuid);

    return [
      position && {
        ...position,
        label: `Должность: ${position.name}`,
        value: `position_uuid`,
      },
      unit && {
        ...unit,
        label: `Подразделение: ${unit.name}`,
        value: `unit_uuid`,
      },
      show_dismissed && {
        label: `Показывать уволенных`,
        value: `show_dismissed`,
      },
    ].filter(Boolean);
  }, [tableParams, positions, subdivisions]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    try {
      const [{ units }, { positions }] = await Promise.all([
        getUnitsOptions(),
        getPositionsOptions({ deleted: false }),
      ]);

      setUnits(units);
      setPositions(positions);

      // Инициализация параметров из localStorage
      const localParams =
        JSON.parse(
          localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`),
        ) || initialTableParams;

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
      is_dismissed: currentParams.filters.show_dismissed ? "" : false,
    };

    delete params.show_dismissed;

    // show_dismissed - показывать уволенных вместе с работающими / показывать только работающих
    // Уволенные is_dismissed, три варианта:
    // 1.1) ничего не присылаешь - отдаю тебе всех сотрудников (и уволенных, и работающих)
    // 1.2) is_dismissed=true - уволенные
    // 1.3) is_dismissed=false - действительные

    try {
      const { employees, total, page, perPage } = await getList(params);
      setData(employees);
      const currentParams = tParams || tableParams;
      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params),
    );
  };

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      ...tableParams.filters,
    });
    setIsFiltersModalShow(true);
  };

  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = (name, val) => {
    setTempValueFilters((prev) => ({
      ...prev,
      [name]: val || null,
    }));
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = () => {
    updateFilters(
      tableParams,
      {
        ...tempValueFilters,
      },
      true,
    );
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  };

  // Очистка всех фильтров
  const clearFilters = () => {
    const updatedTableParams = {
      ...tableParams,
      filters: { ...initialTableParams.filters },
      pagination: {
        ...tableParams.pagination,
        current: 1, // Сбрасываем на первую страницу
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handleDeselectFilter = (value, option) => {
    handleFilterChange(value, null);
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
      },
      true,
    );
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
      ...(sorter?.order
        ? {
            sort: `${sorter.order === "descend" ? "-" : ""}${
              SORT_KEYS[sorter.field]
            }`,
          }
        : {
            sort: null,
          }),
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const updatedTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: shouldResetPage ? 1 : current,
        pageSize,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик добавления нового сотрудника
  const handleAddNew = () => {
    setCurrentItem(null);
    setIsModalShow(true);
  };

  // Обработчик редактирования сотрудника
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения сотрудника
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Обработчик удаления сотрудника
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить сотрудника ${item.firstname} ${item.lastname}?`,
      content:
        "Удаление сотрудника приведет к его исключению из списка сотрудников и он больше не будет отображаться в системе.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем сотрудника после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Сотрудники",
              },
            ]}
          />
          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              Сотрудники
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>
        </Flex>

        <Flex vertical gap={20}>
          <Flex gap={10} align="center">
            <SearchFrom3
              placeholder="Поиск"
              style={{ width: "300px" }}
              defaultValue={tableParams.filters.search}
              onSearch={(search) => handleFilterChange("search", search)}
            />

            <CustomSelect
              placeholder="Рабочая группа"
              mode="multiple"
              options={units}
              fieldNames={{
                label: "name",
                value: "uuid",
                key: "uuid",
              }}
              style={{
                width: 200,
              }}
              optionFilterProp="name"
              allowClear
              maxTagCount="responsive"
              popupMatchSelectWidth={false}
              value={tableParams.filters.units_uuids}
              onChange={(unit) => handleFilterChange("units_uuids", unit)}
            />

            <Button
              type="primary"
              icon={<FilterIcon />}
              onClick={openFiltersModal}
            ></Button>

            {isAnyFilterApplied && (
              <Tooltip title="Очистить все фильтры" placement="bottom">
                <Button
                  color="default"
                  variant="link"
                  shape="circle"
                  size="small"
                  icon={<CloseIcon />}
                  onClick={clearFilters}
                  style={{ background: "#BBBBBB", color: "#fff" }}
                ></Button>
              </Tooltip>
            )}
          </Flex>

          {!!selectedFilters.length && (
            <CustomSelect
              mode="multiple"
              className="select__not-active"
              value={selectedFilters.map((f) => f.value)}
              options={selectedFilters}
              maxTagCount="responsive"
              popupRender={() => null}
              open={false}
              suffixIcon={false}
              showSearch={false}
              onDeselect={handleDeselectFilter}
              style={{ width: "100%" }}
            />
          )}
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <EmployeesTable
            data={data}
            positions={positions}
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            resize
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>

      {isModalShow && (
        <EmployeeModal
          data={currentItem}
          units={subdivisions}
          positions={positions}
          visible={isModalShow}
          onSave={handleSaveItem}
          onCancel={() => setIsModalShow(false)}
        />
      )}

      {isFiltersModalShow && (
        <FiltersModal
          visible={isFiltersModalShow}
          onSave={applyFilters}
          onCancel={() => {
            setIsFiltersModalShow(false);
          }}
          onClear={() => {
            setIsFiltersModalShow(false);
            clearFilters();
          }}
        >
          <Descriptions
            column={1}
            colon={false}
            styles={{
              label: {
                display: "inline-block",
                minWidth: "240px",
                color: "#777777",
              },
              content: {
                display: "block",
                marginLeft: "8px",
              },
            }}
          >
            <Descriptions.Item label="Подразделение">
              <CustomSelect
                placeholder="Начните вводить"
                options={subdivisions}
                fieldNames={{
                  label: "name",
                  value: "uuid",
                  key: "uuid",
                }}
                style={{
                  width: "100%",
                }}
                showSearch
                optionFilterProp="name"
                allowClear
                defaultValue={tempValueFilters.unit_uuid}
                onChange={(unit) => handleTempFilterChange("unit_uuid", unit)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Должность">
              <CustomSelect
                placeholder="Начните вводить"
                options={[NOT_FILLED_OPTION, ...positions]}
                fieldNames={{
                  label: "name",
                  value: "uuid",
                  key: "uuid",
                }}
                style={{
                  width: "100%",
                }}
                showSearch
                optionFilterProp="name"
                allowClear
                defaultValue={tempValueFilters.position_uuid}
                onChange={(position) =>
                  handleTempFilterChange("position_uuid", position)
                }
              />
            </Descriptions.Item>
            <Descriptions.Item label="Показывать уволенных">
              <Switch
                defaultValue={!!tempValueFilters.show_dismissed}
                onChange={(val) =>
                  handleTempFilterChange("show_dismissed", val)
                }
              />
            </Descriptions.Item>
          </Descriptions>
        </FiltersModal>
      )}
    </>
  );
};

export default Employees;
